import React, { useEffect, useState, useCallback } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle, Eye, X, Smartphone, Clock, Filter } from 'lucide-react';
import { fetchCrashReports, resolveCrashReport, CrashReportItem } from '../services/api.ts';
import './CrashReports.css';

const CrashReports: React.FC = () => {
  const [reports, setReports] = useState<CrashReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(7);
  const [filterResolved, setFilterResolved] = useState<boolean | undefined>(false);
  const [selectedReport, setSelectedReport] = useState<CrashReportItem | null>(null);
  const [resolving, setResolving] = useState<number | null>(null);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchCrashReports(days, filterResolved);
      setReports(data);
    } catch (e) {
      console.error('Failed to load crash reports', e);
    } finally {
      setLoading(false);
    }
  }, [days, filterResolved]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleResolve = async (id: number) => {
    setResolving(id);
    try {
      await resolveCrashReport(id);
      setReports(prev => prev.map(r => r.id === id ? { ...r, resolved: true } : r));
      if (selectedReport?.id === id) {
        setSelectedReport(prev => prev ? { ...prev, resolved: true } : null);
      }
    } catch (e) {
      console.error('Failed to resolve crash report', e);
    } finally {
      setResolving(null);
    }
  };

  const unresolvedCount = reports.filter(r => !r.resolved).length;

  return (
    <div className="crash-reports-page">
      <div className="crash-reports-header">
        <div className="crash-reports-title">
          <AlertTriangle size={24} />
          <h1>Crash Reports</h1>
          {unresolvedCount > 0 && (
            <span className="crash-badge">{unresolvedCount}</span>
          )}
        </div>
        <div className="crash-reports-controls">
          <div className="crash-filter-group">
            <Filter size={16} />
            <select value={filterResolved === undefined ? 'all' : filterResolved ? 'resolved' : 'unresolved'}
              onChange={e => {
                const v = e.target.value;
                setFilterResolved(v === 'all' ? undefined : v === 'resolved');
              }}>
              <option value="unresolved">Unresolved</option>
              <option value="all">All</option>
              <option value="resolved">Resolved</option>
            </select>
          </div>
          <div className="crash-filter-group">
            <Clock size={16} />
            <select value={days} onChange={e => setDays(Number(e.target.value))}>
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>
          <button className="crash-refresh-btn" onClick={loadReports} disabled={loading}>
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="crash-loading">Loading crash reports...</div>
      ) : reports.length === 0 ? (
        <div className="crash-empty">
          <CheckCircle size={48} />
          <p>No crash reports found for this period.</p>
        </div>
      ) : (
        <div className="crash-table-container">
          <table className="crash-table">
            <thead>
              <tr>
                <th>Status</th>
                <th>Error</th>
                <th>User</th>
                <th>Platform</th>
                <th>Version</th>
                <th>Time</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reports.map(report => (
                <tr key={report.id} className={report.resolved ? 'resolved' : 'unresolved'}>
                  <td>
                    <span className={`crash-status ${report.resolved ? 'resolved' : 'active'}`}>
                      {report.resolved ? '✓' : '●'}
                    </span>
                  </td>
                  <td className="crash-error-cell">
                    <div className="crash-error-text">{report.error}</div>
                    {report.context && (
                      <div className="crash-context-text">{report.context}</div>
                    )}
                  </td>
                  <td className="crash-user-cell">
                    {report.user_name || report.user_id || '—'}
                  </td>
                  <td>
                    <span className="crash-platform">
                      <Smartphone size={14} />
                      {report.platform || '—'}
                    </span>
                  </td>
                  <td>{report.app_version || '—'}</td>
                  <td className="crash-time-cell">
                    {report.timestamp ? new Date(report.timestamp).toLocaleString() : '—'}
                  </td>
                  <td className="crash-actions">
                    <button className="crash-action-btn view" onClick={() => setSelectedReport(report)} title="View details">
                      <Eye size={16} />
                    </button>
                    {!report.resolved && (
                      <button
                        className="crash-action-btn resolve"
                        onClick={() => handleResolve(report.id)}
                        disabled={resolving === report.id}
                        title="Mark as resolved"
                      >
                        <CheckCircle size={16} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Detail Modal */}
      {selectedReport && (
        <div className="crash-modal-overlay" onClick={() => setSelectedReport(null)}>
          <div className="crash-modal" onClick={e => e.stopPropagation()}>
            <div className="crash-modal-header">
              <h2>Crash Report #{selectedReport.id}</h2>
              <button className="crash-modal-close" onClick={() => setSelectedReport(null)}>
                <X size={20} />
              </button>
            </div>
            <div className="crash-modal-body">
              <div className="crash-detail-grid">
                <div className="crash-detail-item">
                  <label>Status</label>
                  <span className={`crash-status-badge ${selectedReport.resolved ? 'resolved' : 'active'}`}>
                    {selectedReport.resolved ? 'Resolved' : 'Active'}
                  </span>
                </div>
                <div className="crash-detail-item">
                  <label>User</label>
                  <span>{selectedReport.user_name || selectedReport.user_id || 'Anonymous'}</span>
                </div>
                <div className="crash-detail-item">
                  <label>Platform</label>
                  <span>{selectedReport.platform || '—'}</span>
                </div>
                <div className="crash-detail-item">
                  <label>App Version</label>
                  <span>{selectedReport.app_version || '—'}</span>
                </div>
                <div className="crash-detail-item">
                  <label>Time</label>
                  <span>{selectedReport.timestamp ? new Date(selectedReport.timestamp).toLocaleString() : '—'}</span>
                </div>
              </div>

              <div className="crash-detail-section">
                <label>Error</label>
                <pre className="crash-code-block">{selectedReport.error}</pre>
              </div>

              {selectedReport.context && (
                <div className="crash-detail-section">
                  <label>Context</label>
                  <pre className="crash-code-block">{selectedReport.context}</pre>
                </div>
              )}

              {selectedReport.stack_trace && (
                <div className="crash-detail-section">
                  <label>Stack Trace</label>
                  <pre className="crash-code-block crash-stacktrace">{selectedReport.stack_trace}</pre>
                </div>
              )}
            </div>
            <div className="crash-modal-footer">
              {!selectedReport.resolved && (
                <button
                  className="crash-resolve-btn"
                  onClick={() => handleResolve(selectedReport.id)}
                  disabled={resolving === selectedReport.id}
                >
                  <CheckCircle size={16} />
                  Mark as Resolved
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CrashReports;
